import type { ReactElement, FC, ChangeEvent } from 'react';
import { useEffect, useState } from 'react';

import { Toggle } from '@pp/core/src/components/controls/toggle/Toggle';
import { Icons } from '@pp/core/src/components/icons/iconNames';
import { ModalContainer } from '@pp/core/src/components/modal/modalContainer/ModalContainer';
import { ModalSection } from '@pp/core/src/components/modal/modalSection/ModalSection';
import { Tooltip } from '@pp/core/src/components/tooltip/Tooltip';
import { useAudioTrigger } from '@pp/core/src/controllers/audioController/hooks';
import { Logger } from '@pp/core/src/logger/Logger';
import { L10nText } from '@pp/core/src/services/uiTranslations/reactBindings/L10nText';
import { useCoreDispatch } from '@pp/core/src/store/CoreStore';
import { useBetSlice } from '@pp/core/src/store/slices/bets/selectors';
import { useConfigSlice } from '@pp/core/src/store/slices/config/selectors';
import { toastActions } from '@pp/core/src/store/slices/toast/slice';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';

import { HIGHFLYER_PT } from '../../../config/tableConfigParserOptions';
import { useHighflyerGameStateSlice } from '../../../store/slices/highflyerGame/selectors';
import { highflyerGameStateActions } from '../../../store/slices/highflyerGame/slice';
import { useBetboardCanBeHidden } from '../../gameGrid/hooks';

import styles from './CrashSettingsConnected.module.css';

export const HFSettings: FC = (): ReactElement => {
  const dispatch = useCoreDispatch();

  const isSecondBetBoardToggle = useHighflyerGameStateSlice.getIsSecondBetBoardToggle();
  const audioTrigger = useAudioTrigger();
  const tableVariant = useConfigSlice.getTableVariant();
  const hideMyBets = useHighflyerGameStateSlice.getHfHideMyBets();
  const betboardCanbeHidden = useBetboardCanBeHidden();
  const isAnimationToggle = useHighflyerGameStateSlice.getAnimationToggle();
  const [disableHideMyBets, setDisableHideMyBets] = useState<boolean>(false);
  const totalBet = useBetSlice.totalBet();

  useEffect(() => {
    if (totalBet > 0) {
      setDisableHideMyBets(true);
    } else {
      setDisableHideMyBets(false);
    }
  }, [totalBet]);

  const handleHideBet = (event: ChangeEvent<HTMLInputElement>): void => {
    const { checked } = event.target;
    dispatch(highflyerGameStateActions.setHfHideMyBets(checked));
    if (checked) {
      dispatch(toastActions.removeToastByMessage('HIGH_FLYER_TOAST_DETAILS_VISIBLE'));
      dispatch(toastActions.createToast('HIGH_FLYER_TOAST_HIDE_DETAILS'));
    } else {
      dispatch(toastActions.removeToastByMessage('HIGH_FLYER_TOAST_HIDE_DETAILS'));
      dispatch(toastActions.createToast('HIGH_FLYER_TOAST_DETAILS_VISIBLE'));
    }
  };

  const handleAnimToggle = (checked: boolean): void => {
    dispatch(highflyerGameStateActions.isAnimationToggle());
  };

  const handleToggleBetBoard = (value: boolean): void => {
    Logger.logEvent('click', 'settingsDisableStream', Date.now(), 1, '', false);
    if (isSecondBetBoardToggle !== value) {
      audioTrigger.play({ name: `menuClick` });
      dispatch(highflyerGameStateActions.isSecondBetBoardToggle());
    }
  };
  const active_mods = useModifiers(styles, 'box', {
    [!isSecondBetBoardToggle ? 'one_active' : 'two_active']: true,
    disabled: betboardCanbeHidden === null,
  });

  return (
    <ModalContainer>
      {tableVariant === HIGHFLYER_PT ? null : (
        <ModalSection
          title={<L10nText tk="ANIMATION" />}
          icon={Icons.Video}
          action={
            <Toggle checked={isAnimationToggle} onChange={(event): void => handleAnimToggle(event.target.checked)} />
          }
        ></ModalSection>
      )}

      {tableVariant !== HIGHFLYER_PT ? null : (
        <ModalSection
          title={<L10nText tk="SETTINGS_HIDE_BET_DETAILS" />}
          icon={Icons.Hide}
          disabled={false}
          action={
            <Tooltip text={'HIGH_FLYER_TOOLTIP_ENABLE_SETTING'} disabled={!disableHideMyBets}>
              <Toggle name="betDetails" checked={hideMyBets} disabled={disableHideMyBets} onChange={handleHideBet} />
            </Tooltip>
          }
        ></ModalSection>
      )}
      <ModalSection title={<L10nText tk="CRASH_BET_SPOTS_NUMBER" />} disabled={betboardCanbeHidden === null}>
        <div className={styles.parent}>
          <div className={styles.group}>
            <div
              className={`${styles.box_one} ${styles.box_1} ${active_mods}`}
              onClick={() => handleToggleBetBoard(false)}
            >
              <div className={styles.rect}></div>
              <div className={`${styles.rectSmall} ${styles.box_1} ${active_mods}`}></div>
            </div>
            <div
              className={`${styles.box_one} ${styles.box_2} ${active_mods}`}
              onClick={() => handleToggleBetBoard(true)}
            >
              <div className={styles.rect}></div>
              <div className={styles.rectGroup}>
                <div className={`${styles.rectSmall} ${styles.box_2} ${active_mods}`}></div>
                <div className={`${styles.rectSmall} ${styles.box_2} ${active_mods}`}></div>
              </div>
            </div>
          </div>
        </div>
      </ModalSection>
    </ModalContainer>
  );
};
